import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { jobsEndpoint, profilesEndpoint } from "../constants";
import Typewriter from "../components/Typewriter";

const ApplicantDetails = () => {
  const [applicant, setApplicant] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusUpdateLoading, setStatusUpdateLoading] = useState(false);
  const [notes, setNotes] = useState("");
  const { app_id } = useParams();
  const [accountType, setAccountType] = useState(null);
  const navigate = useNavigate();
  const [showNotesEdit, setShowNotesEdit] = useState(false);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await fetch(profilesEndpoint + "me/", {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Token ${token}`,
          },
        });
        const data = await response.json();
        setAccountType(data.account_type);
      } catch (error) {
        console.error("Error fetching profile:", error);
      }
    };
    fetchProfile();
  }, []);

  useEffect(() => {
    const fetchApplicantDetails = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem("token");

        const response = await axios.get(
          `${jobsEndpoint}applicant_details/${app_id}/`,
          {
            headers: {
              Authorization: `Token ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        setApplicant(response.data);
        setNotes(response.data.application?.notes || "");

        try {
          const suggestionsResponse = await axios.get(
            `${jobsEndpoint}${app_id}/suggestions/`,
            {
              headers: {
                Authorization: `Token ${token}`,
                "Content-Type": "application/json",
              },
            }
          );
          setSuggestions(suggestionsResponse.data.suggestions || []);
        } catch (suggestionError) {
          console.error("Error fetching suggestions:", suggestionError);
          setSuggestions([]);
        }

        setLoading(false);
      } catch (err) {
        setError("Failed to load applicant details.");
        console.error("Error fetching applicant:", err);
        setLoading(false);
      }
    };

    if (app_id) {
      fetchApplicantDetails();
    }
  }, [app_id]);

  const getStatusBadgeColor = (status) => {
    switch (status?.toLowerCase()) {
      case "hired":
        return "bg-green-100 text-green-600 border-green-400";
      case "offer_sent":
        return "bg-purple-100 text-purple-600 border-purple-400";
      case "interview_scheduled":
        return "bg-blue-100 text-blue-600 border-blue-400";
      case "shortlisted":
        return "bg-yellow-100 text-yellow-600 border-yellow-400";
      case "applied":
        return "bg-gray-100 text-gray-600 border-gray-400";
      default:
        return "bg-gray-100 text-gray-600 border-gray-400";
    }
  };

  const getSuggestionScoreColor = (score) => {
    if (score >= 80) return "bg-green-100 text-green-700 border-green-300";
    if (score >= 60) return "bg-yellow-100 text-yellow-700 border-yellow-300";
    if (score >= 40) return "bg-orange-100 text-orange-700 border-orange-300";
    return "bg-red-100 text-red-700 border-red-300";
  };

  const formatStatusDisplay = (status) => {
    switch (status?.toLowerCase()) {
      case "applied":
        return "Applied";
      case "shortlisted":
        return "Shortlisted";
      case "interview_scheduled":
        return "Interview Scheduled";
      case "offer_sent":
        return "Offer Sent";
      case "hired":
        return "Hired";
      default:
        return status || "Applied";
    }
  };

  const handleStatusUpdate = async (newStatus) => {
    try {
      setStatusUpdateLoading(true);
      const token = localStorage.getItem("token");

      const response = await axios.patch(
        `${jobsEndpoint}hire_applicant/${app_id}`,
        { status: newStatus },
        {
          headers: {
            Authorization: `Token ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data.applicant) {
        setApplicant(response.data.applicant);
        alert(`Status updated to ${formatStatusDisplay(newStatus)}`);
      }
      setStatusUpdateLoading(false);
    } catch (err) {
      console.error("Failed to update status:", err);
      if (err.response?.data?.error) {
        alert(err.response.data.error);
      } else {
        alert("Failed to update status. Please try again.");
      }
      setStatusUpdateLoading(false);
    }
  };

  const handleNotesUpdate = async () => {
    try {
      const token = localStorage.getItem("token");

      if (applicant.application?.id) {
        const response = await axios.patch(
          `${jobsEndpoint}edit_applicant/${app_id}`,
          { application: { notes: notes } },
          {
            headers: {
              Authorization: `Token ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (response.data.applicant) {
          setApplicant(response.data.applicant);
          setShowNotesEdit(false);
          alert("Notes updated successfully");
        }
      }
    } catch (err) {
      console.error("Failed to update notes:", err);
      alert("Failed to update notes. Please try again.");
    }
  };

  const downloadFile = async (fileId, filename, fileType = "cv") => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${jobsEndpoint}files/${fileId}/download/`,
        {
          headers: { Authorization: `Token ${token}` },
          responseType: "blob",
        }
      );

      const blob = new Blob([response.data]);
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error("Error downloading file:", error);
      alert(`Failed to download ${filename}`);
    }
  };

  const navigateToJobDetails = (jobId) => {
    navigate(`/job/${jobId}`);
  };

  if (loading) {
    return (
      <div className="lg:p-8">
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">Loading applicant details...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="lg:p-8">
        <div className="flex items-center justify-center h-64">
          <div className="text-red-500">{error}</div>
        </div>
      </div>
    );
  }

  if (!applicant) {
    return (
      <div className="lg:p-8">
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">Applicant not found</div>
        </div>
      </div>
    );
  }

  return (
    <div className="lg:p-8">
      {/* Header */}
      <div className="mb-8 flex justify-between items-start">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <button
              onClick={() => navigate(-1)}
              className="text-gray-500 hover:text-gray-700"
            >
              ← Back
            </button>
          </div>
          <h1 className="text-2xl font-semibold text-gray-900">
            {applicant.username || "Applicant"} - Application #{applicant.id}
          </h1>
          <p className="mt-1 text-sm text-gray-600">
            Applied for {applicant.job?.title} at {applicant.job?.company}
          </p>
        </div>

        <div className="flex items-center gap-4">
          <div className="relative">
            <select
              value={applicant.status || "APPLIED"}
              onChange={(e) => handleStatusUpdate(e.target.value)}
              disabled={statusUpdateLoading}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#19bfb7] focus:border-transparent bg-white"
            >
              <option value="APPLIED">Applied</option>
              <option value="SHORTLISTED">Shortlisted</option>
              <option value="INTERVIEW_SCHEDULED">Interview Scheduled</option>
              <option value="OFFER_SENT">Offer Sent</option>
              <option value="HIRED">Hired</option>
            </select>
          </div>
          <span
            className={`px-3 py-1 text-sm font-medium rounded-full border ${getStatusBadgeColor(applicant.status)}`}
          >
            {formatStatusDisplay(applicant.status)}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Basic Information */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Application Information
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">
                  Application Date
                </label>
                <p className="text-gray-900">
                  {applicant.application?.date_created
                    ? new Date(
                        applicant.application.date_created
                      ).toLocaleDateString("en-US", {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      })
                    : new Date(applicant.date_updated).toLocaleDateString(
                        "en-US",
                        { year: "numeric", month: "long", day: "numeric" }
                      )}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">
                  Last Updated
                </label>
                <p className="text-gray-900">
                  {new Date(applicant.date_updated).toLocaleDateString(
                    "en-US",
                    { year: "numeric", month: "long", day: "numeric" }
                  )}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">
                  Job Position
                </label>
                <p className="text-gray-900">{applicant.job?.title || "N/A"}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">
                  Company
                </label>
                <p className="text-gray-900">
                  {applicant.job?.company || "N/A"}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">
                  Location
                </label>
                <p className="text-gray-900">
                  {applicant.job?.location || "N/A"}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">
                  Job Type
                </label>
                <p className="text-gray-900">{applicant.job?.type || "N/A"}</p>
              </div>
            </div>
          </div>

          {/* AI Suggestions */}
          {suggestions && suggestions.length > 0 && (
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                AI Job Suggestions
                <span className="ml-2 text-sm font-normal text-gray-600">
                  ({suggestions.length} alternative positions)
                </span>
              </h2>
              <div className="space-y-4">
                {suggestions.map((suggestion, index) => (
                  <div
                    key={suggestion.id}
                    className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <h3 className="font-medium text-gray-900 mb-1">
                          {suggestion.job?.title || "Unknown Position"}
                        </h3>
                        <p className="text-sm text-gray-600 mb-2">
                          {suggestion.job?.company || applicant.job?.company} •{" "}
                          {suggestion.job?.location || "Location not specified"}
                        </p>
                        <div className="flex items-center gap-3">
                          <span
                            className={`px-2 py-1 text-xs font-medium rounded-full border ${getSuggestionScoreColor(suggestion.ai_score)}`}
                          >
                            {suggestion.ai_score}/100 Match
                          </span>
                          <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-700 rounded-full">
                            {suggestion.ai_rating || "No Rating"}
                          </span>
                        </div>
                      </div>
                      <button
                        onClick={() =>
                          navigateToJobDetails(suggestion.job?.id)
                        }
                        className="ml-4 px-3 py-1 text-sm text-[#19bfb7] hover:text-[#18ada6] font-medium border border-[#19bfb7] hover:border-[#18ada6] rounded-lg transition-colors"
                      >
                        View Job
                      </button>
                    </div>

                    {suggestion.ai_review && (
                      <div className="mt-3">
                        <div className="bg-gray-50 rounded-lg p-3">
                          <h4 className="text-sm font-medium text-gray-800 mb-2">
                            AI Analysis
                          </h4>
                          <div className="text-sm text-gray-700">
                            <Typewriter text={suggestion.ai_review} speed={20} />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* AI Analysis */}
          {(applicant.ai_review ||
            applicant.ai_rating ||
            applicant.ai_critism ||
            applicant.ai_recommendation ||
            applicant.ai_advantages) && (
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                AI Analysis
                {applicant.ai_score && (
                  <span
                    className={`ml-3 px-3 py-1 text-sm font-medium rounded-full border ${getSuggestionScoreColor(applicant.ai_score)}`}
                  >
                    {applicant.ai_score}/100
                  </span>
                )}
              </h2>
              <div className="space-y-4">
                {applicant.ai_review && (
                  <div>
                    <h3 className="text-md font-medium text-gray-800 mb-2">
                      AI Review
                    </h3>
                    <Typewriter text={applicant.ai_review} />
                  </div>
                )}
                {applicant.ai_rating && (
                  <div>
                    <h3 className="text-md font-medium text-gray-800 mb-2">
                      AI Rating
                    </h3>
                    <Typewriter text={applicant.ai_rating} />
                  </div>
                )}
                {applicant.ai_critism && (
                  <div>
                    <h3 className="text-md font-medium text-gray-800 mb-2">
                      AI Criticism
                    </h3>
                    <Typewriter text={applicant.ai_critism} />
                  </div>
                )}
                {applicant.ai_recommendation && (
                  <div>
                    <h3 className="text-md font-medium text-gray-800 mb-2">
                      AI Recommendation
                    </h3>
                    <Typewriter text={applicant.ai_recommendation} />
                  </div>
                )}
                {applicant.ai_advantages && (
                  <div>
                    <h3 className="text-md font-medium text-gray-800 mb-2">
                      AI Advantages
                    </h3>
                    <Typewriter text={applicant.ai_advantages} />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Form Answers */}
          {applicant.form_answers && (
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                Application Responses
              </h2>
              <div className="prose max-w-none">
                <p className="text-gray-700 whitespace-pre-wrap">
                  {applicant.form_answers}
                </p>
              </div>
            </div>
          )}

          {/* Files */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Submitted Files
            </h2>

            {applicant.cv && applicant.cv.length > 0 && (
              <div className="mb-6">
                <h3 className="text-md font-medium text-gray-800 mb-3">
                  CV Files ({applicant.cv.length})
                </h3>
                <div className="space-y-2">
                  {applicant.cv.map((file, index) => (
                    <div
                      key={file.id || index}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center">
                          <span className="text-red-600 text-sm font-medium">
                            📄
                          </span>
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">
                            {file.name || `CV_${index + 1}.pdf`}
                          </p>
                          <p className="text-sm text-gray-500">
                            {file.file_size
                              ? `${(file.file_size / 1024 / 1024).toFixed(2)} MB`
                              : "Size unknown"}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() =>
                          downloadFile(
                            file.id,
                            file.name || `CV_${index + 1}.pdf`,
                            "cv"
                          )
                        }
                        className="px-3 py-1 text-sm text-[#19bfb7] hover:text-[#18ada6] font-medium"
                      >
                        Download
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {applicant.cover_letter && applicant.cover_letter.length > 0 && (
              <div className="mb-6">
                <h3 className="text-md font-medium text-gray-800 mb-3">
                  Cover Letters ({applicant.cover_letter.length})
                </h3>
                <div className="space-y-2">
                  {applicant.cover_letter.map((file, index) => (
                    <div
                      key={file.id || index}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                          <span className="text-green-600 text-sm font-medium">
                            📝
                          </span>
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">
                            {file.name || `Cover_Letter_${index + 1}.pdf`}
                          </p>
                          <p className="text-sm text-gray-500">
                            {file.file_size
                              ? `${(file.file_size / 1024 / 1024).toFixed(2)} MB`
                              : "Size unknown"}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() =>
                          downloadFile(
                            file.id,
                            file.name || `Cover_Letter_${index + 1}.pdf`,
                            "cover_letter"
                          )
                        }
                        className="px-3 py-1 text-sm text-[#19bfb7] hover:text-[#18ada6] font-medium"
                      >
                        Download
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {applicant.extra_form_file &&
              applicant.extra_form_file.length > 0 && (
                <div>
                  <h3 className="text-md font-medium text-gray-800 mb-3">
                    Additional Files ({applicant.extra_form_file.length})
                  </h3>
                  <div className="space-y-2">
                    {applicant.extra_form_file.map((file, index) => (
                      <div
                        key={file.id || index}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                            <span className="text-blue-600 text-sm font-medium">
                              📎
                            </span>
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">
                              {file.name || `Extra_File_${index + 1}`}
                            </p>
                            <p className="text-sm text-gray-500">
                              {file.file_size
                                ? `${(file.file_size / 1024 / 1024).toFixed(2)} MB`
                                : "Size unknown"}
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() =>
                            downloadFile(
                              file.id,
                              file.name || `Extra_File_${index + 1}`,
                              "extra"
                            )
                          }
                          className="px-3 py-1 text-sm text-[#19bfb7] hover:text-[#18ada6] font-medium"
                        >
                          Download
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

            {(!applicant.cv || applicant.cv.length === 0) &&
              (!applicant.cover_letter ||
                applicant.cover_letter.length === 0) &&
              (!applicant.extra_form_file ||
                applicant.extra_form_file.length === 0) && (
                <p className="text-gray-500 text-center py-8">
                  No files uploaded
                </p>
              )}
          </div>
        </div>

      <div className="space-y-6">
  {/* Quick Actions - only shown for non-linka accounts */}
  {accountType !== "linka" && (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <h2 className="text-lg font-semibold text-gray-900 mb-4">
        Quick Actions
      </h2>

      <div className="space-y-3">
        <button
          onClick={() => handleStatusUpdate("HIRED")}
          disabled={statusUpdateLoading || applicant.status === "HIRED"}
          className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-300 disabled:text-gray-500"
        >
          Hire Applicant
        </button>

        <button
          onClick={() =>
            navigate(`/create_offer/${applicant.job.id}/${applicant.id}`)
          }
          disabled={
            statusUpdateLoading || applicant.status === "OFFER_SENT"
          }
          className="w-full px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-300 disabled:text-gray-500"
        >
          Send Offer
        </button>

        <button
          onClick={() =>
            navigate(`/create_interview/${applicant.job.id}/${applicant.id}`)
          }
          disabled={
            statusUpdateLoading ||
            applicant.status === "INTERVIEW_SCHEDULED"
          }
          className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:text-gray-500"
        >
          Schedule Interview
        </button>

        <button
          onClick={() => handleStatusUpdate("SHORTLISTED")}
          disabled={
            statusUpdateLoading || applicant.status === "SHORTLISTED"
          }
          className="w-full px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 disabled:bg-gray-300 disabled:text-gray-500"
        >
          Add to Shortlist
        </button>

        <button
          onClick={() => navigate("/chat")}
          className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
        >
          Chat with candidate
        </button>

        <button
          onClick={() => navigate(`/submission/${app_id}`)}
          className="w-full px-4 py-2 bg-[#19bfb7] text-white rounded-lg hover:bg-[#18ada6]"
        >
          Submit to Client
        </button>
      </div>
    </div>
  )}
          {/* Notes */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Notes</h2>
              <button
                onClick={() => setShowNotesEdit(!showNotesEdit)}
                className="text-sm text-[#19bfb7] hover:text-[#18ada6] font-medium"
              >
                {showNotesEdit ? "Cancel" : "Edit"}
              </button>
            </div>

            {showNotesEdit ? (
              <div className="space-y-3">
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#19bfb7] focus:border-transparent"
                  placeholder="Add notes about this applicant..."
                />
                <div className="flex gap-2">
                  <button
                    onClick={handleNotesUpdate}
                    className="px-4 py-2 bg-[#19bfb7] text-white rounded-lg hover:bg-[#18ada6] text-sm"
                  >
                    Save
                  </button>
                  <button
                    onClick={() => {
                      setNotes(applicant.application?.notes || "");
                      setShowNotesEdit(false);
                    }}
                    className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div>
                {notes ? (
                  <p className="text-gray-700 whitespace-pre-wrap">{notes}</p>
                ) : (
                  <p className="text-gray-500 italic">No notes added yet</p>
                )}
              </div>
            )}
          </div>

          {/* Application Timeline */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Timeline
            </h2>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-[#19bfb7] rounded-full mt-2"></div>
                <div>
                  <p className="font-medium text-gray-900">
                    Application Submitted
                  </p>
                  <p className="text-sm text-gray-500">
                    {applicant.application?.date_created
                      ? new Date(
                          applicant.application.date_created
                        ).toLocaleDateString("en-US", {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })
                      : new Date(applicant.date_updated).toLocaleDateString(
                          "en-US",
                          {
                            year: "numeric",
                            month: "long",
                            day: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          }
                        )}
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-yellow-500 rounded-full mt-2"></div>
                <div>
                  <p className="font-medium text-gray-900">
                    Status: {formatStatusDisplay(applicant.status)}
                  </p>
                  <p className="text-sm text-gray-500">
                    {new Date(applicant.date_updated).toLocaleDateString(
                      "en-US",
                      {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      }
                    )}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* END SIDEBAR */}
      </div>
    </div>
  );
};

export default ApplicantDetails;